import React, {useEffect, useMemo, useState} from 'react';

import uniqBy from 'lodash/uniqBy';
import {
  parse as parseTemplate,
  render as renderTemplate,
  defaultConfig as squirrellyConfig,
} from 'squirrelly';

import {
  createFormErrorsContext,
  type FormErrorsProps,
  ShowNonFieldFormErrors,
} from 'app/general/forms';
import {DelayButton} from 'app/general/widgets/DelayButton';
import {AutomationForm} from 'app/procedures/components/ProcedureEditPage/ActiveEditableProcedureStep/EditableProcedureAutomationFooter';
import {FieldLastEditStamp} from 'app/procedures/components/step-form-fields/FieldLastEditStamp';
import {StepTaskTextField} from 'app/procedures/components/step-form-fields/text-field';
import {StepAutomationTitle} from 'app/procedures/components/structures';
import {useRunAutomation, useUpdateTaskInstance} from 'app/tasks/api';
import {AUTOMATION_RETRY_LIMIT} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import type {
  BasicUserSerializedType,
  TaskInstanceSerializedType,
} from 'sweet-types';
import type {
  AutomationType,
  ProcedureAutomationStepType,
} from 'sweet-types/procedure/procedure-step';

interface Props extends FormErrorsProps {
  task: TaskInstanceSerializedType;
  step: ProcedureAutomationStepType;
  isCompleted: boolean;
  automation: {
    fields?: Array<{name: string; value: string}>;
    updated_at?: string;
    updated_by?: BasicUserSerializedType;
  };
}
export const TaskAutomation = createFormErrorsContext(function TaskAutomation({
  step,
  task,
  isCompleted,
  automation,
  captureFormErrorsFromRejection,
  setFormErrors,
}: Props) {
  const [userInput, setUserInput] = React.useState<Array<FormVariable>>([]);
  const [updateTaskInstance] = useUpdateTaskInstance();
  const [runAutomation, {isLoading: isAutomationRunning, error}] =
    useRunAutomation();

  useEffect(() => {
    if (error) {
      captureFormErrorsFromRejection(error);
    } else {
      setFormErrors({});
    }
  }, [error]);

  const initialValues: Record<string, string> = useMemo(() => {
    const values: Record<string, string> = {};
    automation.fields?.forEach((field) => {
      values[field.name] = field.value;
    });

    return values;
  }, [automation]);

  const renderedAutomation: AutomationType = useMemo(() => {
    if (userInput.length) {
      const template = JSON.stringify(step.automation);
      const context = Object.fromEntries(
        userInput.map(({name, value}) => [
          name,
          value === '' ? `{{ ${name} }}` : value,
        ])
      );
      const rendered = renderTemplate(template, context, {useWith: true});
      return JSON.parse(rendered);
    }
    return step.automation;
  }, [userInput]);

  function handlePlaceholderInput(value: string, name: string) {
    setUserInput((prevPlaceholders) =>
      prevPlaceholders.map((placeholder) =>
        placeholder.name === name ? {...placeholder, value: value} : placeholder
      )
    );
  }

  function handleSubmit() {
    const hashid = task.template.assigned_object.hashid;
    return updateTaskInstance({
      url: task.url,
      update: {
        progress: {
          [hashid]: {
            steps: [
              {
                set_automation_field: userInput,
                id: step.id,
                step: step.n,
              },
            ],
          },
        },
      },
    }).unwrap();
  }

  useEffect(() => {
    // pull the variables from the given automation template, we browse all fields at once.
    const parsed = parseTemplate(
      JSON.stringify(step.automation),
      squirrellyConfig
    );

    setUserInput(
      uniqBy(
        parsed
          .map((item: string | {c?: string}) => {
            const name =
              typeof item === 'object' && 'c' in item ? item.c : undefined;
            return {
              name,
              value: initialValues[name ?? ''] ?? '',
            };
          })
          .filter((obj) => obj.name != undefined) as Array<FormVariable>,
        'name'
      )
    );
  }, []);

  return (
    <div className="pb-4">
      <StepAutomationTitle className="mt-3" title={gt('Email Automation')} />
      <div className="card">
        <div className="card-header">{gt('Automation Form')}</div>
        <div className="card-body">
          {userInput.map(({name, value}) => (
            <AutomationPlaceholderInput
              key={name}
              name={name}
              value={value}
              initialValue={initialValues[name]}
              onChange={handlePlaceholderInput}
              onSubmit={handleSubmit}
            />
          ))}
          <FieldLastEditStamp progress={automation} />
        </div>
      </div>
      <hr />
      <div className="card">
        <div className="card-header">{gt('Preview')}</div>
        <div className="card-body">
          <AutomationForm edit={false} automation={renderedAutomation} />
          <ShowNonFieldFormErrors errorSelector={(errors) => errors?.step} />
          <div className="text-right">
            <DelayButton
              className="mt-4 btn btn-primary"
              isIdle={!isAutomationRunning}
              busyText={gt('Running...')}
              delayTooltip={gt(
                'Please wait a few minutes before running this again.'
              )}
              onClick={() =>
                runAutomation({taskinstance: task.url, step_id: step.id})
              }
              isResponseSuccessful={(response) =>
                response?.data.status === 'completed'
              }
              duration={AUTOMATION_RETRY_LIMIT}
            >
              {isCompleted ? gt('Run Again') : gt('Run')}
            </DelayButton>
          </div>
        </div>
      </div>
    </div>
  );
});

type AutomationPlaceholderInputProps = {
  name: string;
  value: string;
  initialValue: string;
  onChange: (value: string, name: string) => void;
  onSubmit: () => Promise<unknown>;
};

export function AutomationPlaceholderInput({
  name,
  value,
  initialValue,
  onChange,
  onSubmit,
}: AutomationPlaceholderInputProps) {
  const [isSaving, setIsSaving] = useState(false);
  const handlePlaceholderSubmit = () => {
    setIsSaving(true);
    onSubmit()
      .catch(() => {
        // ignore error, RTK already helps to send notification
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="mb-3">
      <StepTaskTextField
        field={{type: 'text', id: name, label: name}}
        value={value}
        onSubmit={handlePlaceholderSubmit}
        inputValueChanged={(event) => onChange(event.target.value, name)}
        isSaving={isSaving}
        initial={initialValue}
        blocked={isSaving}
      />
    </div>
  );
}
interface FormVariable {
  name: string;
  value: string;
}
